/**
 * Which screens a class has already worked through.
 *
 * Per-device rather than per-child, because that is what the hardware is: one
 * panel at the front of one classroom. There is no login and there should not
 * be one - the RPPM describes children taking turns at the board, not each
 * holding an account.
 *
 * Every access is guarded the same way `shell/pwa.ts` guards its own: private
 * mode, cleared storage and corrupt JSON all degrade to "nothing recorded"
 * rather than taking the screen down. Losing progress is a small annoyance;
 * a lesson that will not open in front of a class is not.
 */

const PREFIX = 'ifp.module.';

const key = (moduleId: string) => `${PREFIX}${moduleId}`;

export function readCompleted(moduleId: string): Set<string> {
  try {
    const raw = localStorage.getItem(key(moduleId));
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

export function markCompleted(moduleId: string, activityId: string): void {
  const done = readCompleted(moduleId);
  if (done.has(activityId)) return;
  done.add(activityId);
  try {
    localStorage.setItem(key(moduleId), JSON.stringify([...done]));
  } catch {
    // Full or blocked. The step still reads as done for the rest of the
    // session; it simply will not survive a reload.
  }
}

export function resetModule(moduleId: string): void {
  try {
    localStorage.removeItem(key(moduleId));
  } catch {
    // Nothing to do.
  }
}
